














































import { AxiosError } from 'axios';
import { mapActions, mapGetters, mapState } from 'vuex';
import { PropType } from 'vue';

import {
  createWebAiStreamer,
  updateWebAiStreamer,
  validateStreamer
} from '@/api/CopyStake/Streamers';
import { TStreamer, EStreamerType, EStreamingType } from '@/api/schema';

import * as vr from '@/helpers/validation';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import { LANGUAGES } from '@/helpers/langHelpers';

import Tooltip from '@/components/tooltip/Tooltip.vue';
import CopyStakeWebAiStreamerProfileForm from '@/views/CopyStake/CopyStakeAiStreamer/WebAiStreamerProfiles/CopyStakeWebAiStreamerProfileForm.vue';

export default {
  name: 'CopyStakeWebAiStreamerProfileEditDialog',
  components: {
    Tooltip,
    CopyStakeWebAiStreamerProfileForm
  },
  props: {
    value: {
      type: Boolean as PropType<boolean>,
      default: (): boolean => false
    },
    streamer: {
      type: Object as PropType<TStreamer>,
      default: null
    }
  },
  data(): any {
    return {
      streamerTypes: [
        { value: EStreamerType.PRIVATE, label: 'Private' },
        { value: EStreamerType.PUBLIC, label: 'Public' }
      ],

      formData: {
        nickname: '',
        operatorUserId: '',
        accessCode: '',
        type: '',
        shortBio: '',
        login: '',
        password: '',
        language: ''
      },

      serverError: {
        nickname: null,
        operatorUserId: null,
        accessCode: null,
        type: null,
        shortBio: null,
        language: null
      },

      processing: false
    };
  },
  watch: {
    async value(val: boolean): Promise<void> {
      if (val) {
        await this.resetForm();

        if (!!this.streamer && !!Object.keys(this.streamer).length) {
          this.formData = {
            nickname: this.streamer.nickname,
            operatorUserId: this.streamer.operatorUserId,
            accessCode: this.streamer.accessCode,
            type: this.streamer.type,
            shortBio: this.streamer.shortBio,
            login: this.streamer.webAiStreamerLogin,
            password: this.streamer.webAiStreamerPassword,
            language: this.streamer.language
          };
        }
      }
    },
    operatorId: {
      handler(id: number): void {
        if (id) {
          this.getOperatorLanguages();
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('Operator', ['languages']),
    ...mapGetters('Onboarding', [
      'operatorId',
      'isUndergroundCasinoGamesIntegrationType'
    ]),
    isDialogVisible: {
      get(): boolean {
        return this.value;
      },
      set(value: boolean): void {
        this.$emit('input', value);
      }
    },
    rules(): any {
      return {
        nickname: [
          vr.required,
          (v: string) => vr.maxLength(v, 20),
          (v: string) =>
            vr.validByRegExp(
              v,
              /^[A-Za-z0-9\s_.-]{2,20}$/,
              'Only latin letters, number, blank space and symbols ( - _ . ) available'
            )
        ],
        operatorUserId: [vr.required],
        accessCode: [
          (v: string) => {
            if (this.formData.type === EStreamerType.PRIVATE) {
              return vr.required(v);
            }

            return true;
          },
          (v: string) => {
            if (!v) return true;

            return !v.includes(' ') || 'No blank space available';
          },
          (v: string) => {
            if (!v) return true;

            return vr.validByRegExp(
              v,
              /^[0-9A-Z]{6}$/i,
              'Access code field must contains only numbers and A-Z'
            );
          },
          (v: string) => {
            if (!v) return true;

            return vr.minLength(v, 6);
          },
          (v: string) => {
            if (!v) return true;

            return vr.maxLength(v, 6);
          }
        ],
        type: [vr.required],
        shortBio: [
          (v: string) => {
            if (!v) return true;

            return vr.maxLength(v, 140);
          }
        ],
        language: [vr.required],

        login: [(v: string) => vr.required(v, 'Login')],
        password: [(v: string) => vr.required(v, 'Password')]
      };
    },
    isEdit(): boolean {
      return !!this.streamer?.id;
    },
    header(): string {
      return this.isEdit
        ? 'Edit Streamer Profile'
        : 'Create New Streamer Profile';
    },
    submitText(): string {
      return this.isEdit ? 'Save Changes' : 'Add Streamer Profile';
    },
    operatorLanguages(): any {
      return LANGUAGES.filter((lang) => this.languages.includes(lang.value));
    }
  },
  methods: {
    ...mapActions('Operator', ['getOperatorLanguages']),
    async handlerOnSubmit(): Promise<void> {
      try {
        this.resetServerErrors();
        const isValid = await (this.$refs.streamerForm as any).validate();

        if (!isValid || this.processing) return;
        this.processing = true;

        const payload = {
          ...this.formData,
          accessCode:
            (this.formData.accessCode &&
              this.formData.accessCode.toUpperCase()) ||
            null
        };

        if (this.isUndergroundCasinoGamesIntegrationType) {
          payload.login = null;
          payload.password = null;
        }

        if (this.isEdit) {
          await updateWebAiStreamer(this.streamer.id, payload);

          this.$toast.success(
            `Streamer "${payload.nickname}" has been successfully updated`
          );
        } else {
          await validateStreamer({
            ...payload,
            streamingType: EStreamingType.WEB_AI
          });

          await createWebAiStreamer(payload);

          this.$toast.success(
            `Streamer "${payload?.nickname}" has been successfully created`
          );
        }

        this.closeDialog(this.isEdit);

        this.$emit('submit');
      } catch (error) {
        this.parseServerErrors(error);
        errorToastMessage(error);
      } finally {
        this.processing = false;
      }
    },
    parseServerErrors(error: AxiosError): void {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message;
        if (!errorMessage) return;

        if (
          errorMessage.includes('Streamer with access code') ||
          errorMessage.includes('Streamer with accessCode')
        ) {
          this.serverError.accessCode = errorMessage;
        }

        if (errorMessage.includes('Streamer with nickname')) {
          this.serverError.nickname = errorMessage;
        }

        if (errorMessage.includes('Streamer with user')) {
          this.serverError.operatorUserId = errorMessage;
        }

        if (errorMessage.includes('User by operatorUserId')) {
          this.serverError.operatorUserId = errorMessage;
        }

        if (errorMessage.includes('shortBio')) {
          this.serverError.shortBio = errorMessage;
        }
      }
    },
    closeDialog(clearData: boolean = true): void {
      this.isDialogVisible = false;
      this.$emit('close', clearData);
    },
    resetServerErrors(): void {
      this.serverError = {
        nickname: null,
        accessCode: null,
        operatorUserId: null,
        type: null,
        shortBio: null,
        language: null
      };
    },
    async resetForm(): Promise<void> {
      this.formData = {
        nickname: '',
        operatorUserId: '',
        accessCode: '',
        type: '',
        shortBio: '',
        login: '',
        password: '',
        language: ''
      };

      this.resetServerErrors();
      await (this.$refs.streamerForm as any)?.reset();
    }
  }
};
